import { useState, useEffect, useRef } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  House,
  Briefcase,
  Building2,
  ListTodo,
  CheckSquare,
  LogOut,
  UserCircle,
  Users as UsersIcon,
  PanelLeft,
  Menu,
  X,
  Calendar,
  Plus,
  StickyNote,
  Ticket,
  Workflow,
} from 'lucide-react';
import NotificationBell from './NotificationBell.jsx';
import ThemeToggle from './ThemeToggle.jsx';
import DensityToggle from './DensityToggle.jsx';
import SidebarItem from './SidebarItem.jsx';
import GlobalCalendarDrawer from './GlobalCalendarDrawer.jsx';
import NoteFormModal from './NoteFormModal.jsx';
import TaskFormModal from './tasks/TaskFormModal.jsx';
import TicketFormModal from './tickets/TicketFormModal.jsx';
import DraftReviewModal from './DraftReviewModal.jsx';
import useAuthStore from '../store/useAuthStore.js';
import useUIStore from '../store/useUIStore.js';
import { getProject } from '../api/project.service.js';

const navigation = [
  { name: 'Home', href: '/dashboard', icon: House },
  { name: 'Calendar', href: '/calendar', icon: Calendar },
  { name: 'Tasks Dashboard', href: '/tasks', icon: ListTodo },
  { name: 'Tickets', href: '/tickets', icon: Ticket },
  { name: 'My Todos', href: '/todos', icon: CheckSquare },
  { name: 'Prototypes', href: '/prototypes', icon: Workflow },
  {
    name: 'Projects',
    href: '/projects',
    icon: Briefcase,
    roles: ['ADMIN', 'DEPARTMENT_HEAD', 'INTERNAL'],
  },
  {
    name: 'Departments',
    href: '/departments',
    icon: Building2,
    roles: ['ADMIN', 'DEPARTMENT_HEAD'],
  },
  {
    name: 'Users',
    href: '/users',
    icon: UsersIcon,
    roles: ['ADMIN'],
  },
  { name: 'Profile', href: '/profile', icon: UserCircle },
];

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const {
    isSidebarOpen,
    isMobileOpen,
    toggleSidebar,
    toggleMobileSidebar,
    isGlobalNoteModalOpen,
    isGlobalTaskModalOpen,
    isGlobalTicketModalOpen,
    globalModalProjectId,
    setGlobalNoteModalOpen,
    setGlobalTaskModalOpen,
    setGlobalTicketModalOpen,
  } = useUIStore();
  const [isCalendarDrawerOpen, setCalendarDrawerOpen] = useState(false);
  const [isAddDropdownOpen, setIsAddDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsAddDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Parse project ID from URL bar
  const projectMatch = location.pathname.match(/^\/projects\/([^\/]+)/);
  const currentProjectId = projectMatch ? projectMatch[1] : null;

  // Fetch project details to check access & role
  const { data: currentProject } = useQuery({
    queryKey: ['project', currentProjectId],
    queryFn: async () => {
      const { data } = await getProject(currentProjectId);
      return data.data;
    },
    enabled: !!currentProjectId && !!user,
  });

  const isAdmin = user?.globalRole === 'ADMIN';
  const isProjectMember = Boolean(
    user &&
    currentProject &&
    (isAdmin ||
      currentProject.assignments?.some((assignment) => assignment.userId === user.id))
  );

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const filteredNavigation = navigation.filter(
    (item) => !item.roles || item.roles.includes(user?.globalRole)
  );

  return (
    <div className="flex h-screen bg-background text-foreground overflow-hidden">
      {/* Mobile Sidebar Overlay */}
      {isMobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={toggleMobileSidebar} />
      )}

      {/* Sidebar */}
      <div
        className={`fixed lg:static inset-y-0 left-0 z-50 bg-card border-r border-border flex flex-col shrink-0 transition-all duration-300 ease-in-out ${
          isMobileOpen ? 'translate-x-0 w-64' : '-translate-x-full lg:translate-x-0'
        } ${isSidebarOpen ? 'lg:w-64' : 'lg:w-20'}`}
      >
        <div
          className="flex items-center justify-between border-b border-border px-4 shrink-0"
          style={{ minHeight: 'var(--size-topbar)' }}
        >
          {/* Logo or Icon based on state */}
          {isSidebarOpen || isMobileOpen ? (
            <img
              src="/icons/logo-name-nbg.png"
              alt="ManageMate"
              className="h-10 object-contain transition-opacity duration-300 opacity-100"
            />
          ) : (
            <img
              src="/icons/logo-light-nbg.png"
              alt="ManageMate"
              className="h-10 object-contain transition-opacity duration-300 mx-auto"
            />
          )}

          {/* Mobile Close Button */}
          <button
            className="lg:hidden p-1 text-muted-foreground hover:text-foreground"
            onClick={toggleMobileSidebar}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="nav-shell flex-1 space-y-2 overflow-y-auto overflow-x-hidden">
          {filteredNavigation.map((item) => {
            const isActive =
              location.pathname === item.href ||
              (location.pathname.startsWith(item.href) &&
                item.href !== '/' &&
                item.name !== 'Projects');
            // If sidebar is collapsed (mini), we don't pass submenus
            return (
              <SidebarItem
                key={item.name}
                item={item}
                isActive={isActive}
                isCollapsed={!isSidebarOpen && !isMobileOpen}
              />
            );
          })}
        </nav>

        <div className="panel-section border-t border-border flex flex-col items-center overflow-hidden">
          {isSidebarOpen || isMobileOpen ? (
            <div className="w-full">
              <div className="mb-3 panel-section bg-background rounded-xl border border-border">
                <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                  User
                </p>
                <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {user?.globalRole?.replace('_', ' ')}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="nav-item-density flex w-full items-center text-sm font-medium text-muted-foreground rounded-lg hover:bg-destructive hover:text-destructive-foreground transition-colors"
              >
                <LogOut className="density-icon opacity-70" />
                Logout
              </button>
            </div>
          ) : (
            <button
              onClick={handleLogout}
              className="control-inline-compact text-muted-foreground hover:bg-destructive hover:text-destructive-foreground rounded-lg transition-colors"
              title="Logout"
            >
              <LogOut className="density-icon opacity-70" />
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 bg-background transition-all duration-300 ease-in-out">
        <header
          className="bg-card border-b border-border flex items-center px-4 sm:px-6 shadow-sm shrink-0"
          style={{ minHeight: 'var(--size-topbar)' }}
        >
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Mobile Hamburger */}
            <button
              onClick={toggleMobileSidebar}
              className="control-inline-compact -ml-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors lg:hidden"
            >
              <Menu className="density-icon" />
            </button>

            {/* Desktop Panel Toggle */}
            <button
              onClick={toggleSidebar}
              className="hidden lg:block control-inline-compact -ml-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
              title={isSidebarOpen ? 'Collapse Sidebar' : 'Expand Sidebar'}
            >
              <PanelLeft className="density-icon" />
            </button>
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="hidden sm:flex items-center gap-2">
              <DensityToggle />
              <ThemeToggle />
            </div>
            <button
              onClick={() => setCalendarDrawerOpen(true)}
              className="control-inline-compact text-muted-foreground hover:bg-accent rounded-lg transition-colors"
              title="Open Global Calendar"
            >
              <Calendar className="density-icon" />
            </button>
            <NotificationBell />
            
            {/* Quick Add Dropdown */}
            <div className="relative flex items-center" ref={dropdownRef}>
              <button
                onClick={() => setIsAddDropdownOpen(!isAddDropdownOpen)}
                className="control-inline-compact bg-primary text-primary-foreground hover:opacity-90 rounded-lg transition-all flex items-center justify-center gap-1.5 px-3 font-semibold cursor-pointer shadow-sm active:scale-95"
                title="Quick Add"
              >
                <Plus className="density-icon" />
                <span className="hidden sm:inline text-xs uppercase tracking-wider">Add</span>
              </button>
              {isAddDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 rounded-xl border border-border bg-card shadow-lg py-1 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                  <button
                    onClick={() => {
                      setIsAddDropdownOpen(false);
                      setGlobalNoteModalOpen(true);
                    }}
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-foreground hover:bg-accent transition-colors text-left cursor-pointer"
                  >
                    <StickyNote className="w-4 h-4 text-muted-foreground" />
                    <span>Add Note</span>
                  </button>
                  
                  {isProjectMember && (
                    <>
                      <button
                        onClick={() => {
                          setIsAddDropdownOpen(false);
                          setGlobalTaskModalOpen(true, currentProjectId);
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-foreground hover:bg-accent transition-colors text-left cursor-pointer"
                      >
                        <CheckSquare className="w-4 h-4 text-muted-foreground" />
                        <span>Add Task</span>
                      </button>
                      <button
                        onClick={() => {
                          setIsAddDropdownOpen(false);
                          setGlobalTicketModalOpen(true, currentProjectId);
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-foreground hover:bg-accent transition-colors text-left cursor-pointer"
                      >
                        <Ticket className="w-4 h-4 text-muted-foreground" />
                        <span>Add Ticket</span>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="hidden sm:block h-6 w-px bg-border mx-1" />
            <Link
              to="/profile"
              className="rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold hover:opacity-90 transition-opacity shrink-0"
              style={{
                width: 'var(--size-compact-control-height)',
                height: 'var(--size-compact-control-height)',
              }}
              title="View Profile"
            >
              {user?.name?.charAt(0) || 'U'}
            </Link>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-page">
          <div className="mx-auto w-full">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Global Calendar Drawer */}
      <GlobalCalendarDrawer
        isOpen={isCalendarDrawerOpen}
        onClose={() => setCalendarDrawerOpen(false)}
      />

      {/* Global Note Modal */}
      <NoteFormModal
        isOpen={isGlobalNoteModalOpen}
        onClose={() => setGlobalNoteModalOpen(false)}
      />

      {/* Global Task Modal */}
      {isGlobalTaskModalOpen && (
        <TaskFormModal
          isOpen={isGlobalTaskModalOpen}
          onClose={() => setGlobalTaskModalOpen(false)}
          defaultProjectId={globalModalProjectId || ''}
        />
      )}

      {/* Global Ticket Modal */}
      {isGlobalTicketModalOpen && (
        <TicketFormModal
          isOpen={isGlobalTicketModalOpen}
          onClose={() => setGlobalTicketModalOpen(false)}
          projectId={globalModalProjectId}
        />
      )}

      {/* Safety Draft Shield */}
      <DraftReviewModal />
    </div>
  );
}
